import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  BanknoteIcon,
  Check,
  CheckCircle2,
  Clock,
  DollarSign,
  Eye,
  FileText,
  Loader2,
  Paperclip,
  TrendingUp,
  X,
} from "lucide-react";
import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { CreateLoaderTable } from "@/components/Loaders";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Mask } from "@/utils/mask";
import { usePermissions } from "@/hooks/usePermissions";
import { TypeUser } from "@/types/userType";
import { PERMISSIONS } from "@/constants/permissions";
import { BLEED_STATUS, Bleed, BleedListResponse } from "@/types/bleed";
import {
  BleedFilters,
  approveBleed,
  getBleedBalance,
  getBleeds,
  getShowBleed,
  rejectBleed,
} from "@/Api/bleeds";
import { getSelectSellers } from "@/Api/sellers";
import { AddBleed } from "../Add";

const STATUS_STYLE: Record<number, string> = {
  [BLEED_STATUS.PENDING]: "bg-yellow-400 text-white",
  [BLEED_STATUS.APPROVED]: "bg-green-500 text-white",
  [BLEED_STATUS.REJECTED]: "bg-red-500 text-white",
};

const DEFAULT_FILTERS: BleedFilters = {
  order_by: "created_at",
  order_direction: "desc",
  per_page: 30,
};

const formatDateOnly = (date?: string | null) => {
  if (!date) return "-";
  const [y, m, d] = date.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

const formatDateTime = (date?: string | null) =>
  date ? new Date(date).toLocaleString("pt-BR") : "-";

interface SellerOption {
  id: number;
  name: string;
}

export function BleedsList() {
  const { user, hasPermission } = usePermissions();

  const isAdmin = user?.user_type_id === TypeUser.admin;
  const isSeller = user?.user_type_id === TypeUser.seller;
  const isRoleUser =
    user?.user_type_id === TypeUser.client || user?.user_type_id === TypeUser.unity_responsible;

  // Espelha o backend: hasPermission libera por padrão vendedor/admin, por isso
  // aprovar/rejeitar/ver todas só para admin ou usuários com role que tenham a permissão.
  const canViewAll = isAdmin || (isRoleUser && hasPermission(PERMISSIONS.VIEW_ALL_BLEEDS));
  const canApprove = isAdmin || (isRoleUser && hasPermission(PERMISSIONS.APPROVE_BLEED));
  const canReject = isAdmin || (isRoleUser && hasPermission(PERMISSIONS.REJECT_BLEED));

  const [listData, setListData] = useState<BleedListResponse | null>(null);
  const [state, setState] = useState<"loading" | "production">("loading");
  const [filters, setFilters] = useState<BleedFilters>(DEFAULT_FILTERS);
  const [sellers, setSellers] = useState<SellerOption[]>([]);
  const [filterSeller, setFilterSeller] = useState<SellerOption | undefined>();
  const [sellerHasBleed, setSellerHasBleed] = useState<boolean | null>(null);

  const [detail, setDetail] = useState<{ open: boolean; loading: boolean; bleed: Bleed | null }>({
    open: false,
    loading: false,
    bleed: null,
  });
  const [review, setReview] = useState<{
    open: boolean;
    action: "approve" | "reject";
    bleed: Bleed | null;
    observation: string;
    saving: boolean;
  }>({ open: false, action: "approve", bleed: null, observation: "", saving: false });

  const fetchData = useCallback(async (page = 1, currentFilters: BleedFilters = DEFAULT_FILTERS) => {
    setState("loading");
    const { data, ok } = await getBleeds(page, currentFilters);
    setListData(ok ? data?.response : null);
    setState("production");
  }, []);

  useEffect(() => {
    fetchData(1, DEFAULT_FILTERS);
  }, [fetchData]);

  // Financeiro/admin: lista de vendedores para filtro e para registrar em nome de alguém
  useEffect(() => {
    if (!canViewAll) return;
    getSelectSellers({ is_active: true }).then(({ data, ok }) => {
      if (!ok) return;
      setSellers(
        (data?.response || []).map((s: any) => ({ id: s.id, name: s?.user?.name || `#${s.id}` }))
      );
    });
  }, [canViewAll]);

  // Vendedor: só pode registrar se a unidade dele usa sangria
  useEffect(() => {
    if (!isSeller) return;
    getBleedBalance().then(({ data, ok }) => {
      setSellerHasBleed(ok ? !!data?.response?.has_bleed : false);
    });
  }, [isSeller]);

  const canCreate =
    (isSeller && sellerHasBleed === true) ||
    isAdmin ||
    (isRoleUser && hasPermission(PERMISSIONS.CREATE_BLEED));

  const applyFilters = () => fetchData(1, filters);

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setFilterSeller(undefined);
    fetchData(1, DEFAULT_FILTERS);
  };

  const goToPage = (page: number) => {
    if (!listData || page < 1 || page > listData.last_page) return;
    fetchData(page, filters);
  };

  const openDetail = async (bleed: Bleed) => {
    setDetail({ open: true, loading: true, bleed });
    const { data, ok } = await getShowBleed(bleed.uuid);
    setDetail({ open: true, loading: false, bleed: ok ? data?.response : bleed });
  };

  const confirmReview = async () => {
    if (!review.bleed) return;
    setReview((r) => ({ ...r, saving: true }));

    const call = review.action === "approve" ? approveBleed : rejectBleed;
    const { ok } = await call(review.bleed.uuid, review.observation.trim() || undefined);

    setReview((r) => ({ ...r, saving: false }));
    if (!ok) return;

    toast.success(review.action === "approve" ? "Sangria aprovada" : "Sangria rejeitada");
    setReview({ open: false, action: "approve", bleed: null, observation: "", saving: false });
    setDetail((d) => ({ ...d, open: false }));
    fetchData(listData?.current_page || 1, filters);
  };

  const summary = listData?.summary;

  return (
    <Main>
      <PageHeader
        icon={BanknoteIcon}
        title="Sangrias"
        description={canViewAll ? "Sangrias de todos os vendedores" : "Suas sangrias e saldo disponível"}
      >
        {canCreate && (
          <AddBleed
            onCreated={() => fetchData(1, filters)}
            sellers={canViewAll && !isSeller ? sellers : undefined}
          />
        )}
      </PageHeader>

      {isSeller && sellerHasBleed === false && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
          Sua unidade não utiliza sangria.
        </div>
      )}

      {/* Filtros */}
      <div className="rounded-lg border bg-zinc-50 dark:bg-[#2c2c2c] p-4 mb-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="space-y-1">
            <Label>Código</Label>
            <Input
              inputMode="numeric"
              maxLength={5}
              placeholder="Código da sangria"
              value={filters.code || ""}
              onChange={(e) =>
                setFilters((f) => ({ ...f, code: e.target.value.replace(/\D/g, "").slice(0, 5) }))
              }
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            />
          </div>

          {canViewAll && (
            <div className="space-y-1">
              <Label>Vendedor</Label>
              <SearchableSelect
                items={sellers}
                searchFields={["name"]}
                displayField="name"
                placeholder="Buscar vendedor..."
                value={filterSeller}
                onSelect={(item) => {
                  setFilterSeller(item);
                  setFilters((f) => ({ ...f, seller_id: [item.id] }));
                }}
              />
            </div>
          )}

          <div className="space-y-1">
            <Label>Status</Label>
            <Select
              value={filters.bleed_status_id || "all"}
              onValueChange={(v) =>
                setFilters((f) => ({ ...f, bleed_status_id: v === "all" ? undefined : v }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos os status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value={String(BLEED_STATUS.PENDING)}>Pendente</SelectItem>
                <SelectItem value={String(BLEED_STATUS.APPROVED)}>Aprovada</SelectItem>
                <SelectItem value={String(BLEED_STATUS.REJECTED)}>Rejeitada</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Data inicial</Label>
            <Input
              type="date"
              value={filters.start_date || ""}
              onChange={(e) => setFilters((f) => ({ ...f, start_date: e.target.value || undefined }))}
            />
          </div>

          <div className="space-y-1">
            <Label>Data final</Label>
            <Input
              type="date"
              value={filters.end_date || ""}
              onChange={(e) => setFilters((f) => ({ ...f, end_date: e.target.value || undefined }))}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={applyFilters}>Filtrar</Button>
          <Button variant="outline" onClick={clearFilters}>
            Limpar
          </Button>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <SummaryCard
          icon={TrendingUp}
          label="Saldo atual"
          value={Mask.formatCurrency(summary?.current_balance ?? 0)}
          className="text-green-600"
        />
        <SummaryCard
          icon={CheckCircle2}
          label="Sangrias aprovadas"
          value={String(summary?.approved_count ?? 0)}
          className="text-green-600"
        />
        <SummaryCard
          icon={DollarSign}
          label="Total aprovado"
          value={Mask.formatCurrency(summary?.approved_total ?? 0)}
        />
        <SummaryCard
          icon={Clock}
          label="Pendentes"
          value={`${summary?.pending_count ?? 0} · ${Mask.formatCurrency(summary?.pending_total ?? 0)}`}
          className="text-amber-600"
        />
      </div>

      {/* Tabela */}
      {state === "loading" ? (
        <CreateLoaderTable cols={7} lines={6} />
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table className="bg-zinc-50 dark:bg-[#2c2c2c] min-w-[760px]">
            <TableHeader>
              <TableRow>
                <TableHead>Data sangria</TableHead>
                <TableHead>Código</TableHead>
                <TableHead>Vendedor</TableHead>
                <TableHead>Valor</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead>Data de criação</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!listData?.data?.length ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    Nenhuma sangria encontrada.
                  </TableCell>
                </TableRow>
              ) : (
                listData.data.map((bleed) => {
                  const isPending = bleed.bleed_status_id === BLEED_STATUS.PENDING;
                  return (
                    <TableRow key={bleed.uuid}>
                      <TableCell>{formatDateOnly(bleed.bleed_date)}</TableCell>
                      <TableCell className="font-bold">{bleed.code}</TableCell>
                      <TableCell>{bleed.seller?.user?.name || "-"}</TableCell>
                      <TableCell className="font-medium">
                        {Mask.formatCurrency(Number(bleed.value))}
                      </TableCell>
                      <TableCell className="text-center">
                        <span
                          className={cn(
                            "inline-block rounded-full px-3 py-1 text-xs font-semibold",
                            STATUS_STYLE[bleed.bleed_status_id] || "bg-gray-400 text-white"
                          )}
                        >
                          {bleed.bleed_status?.name}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{formatDateTime(bleed.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {!!bleed.files_count && (
                            <span
                              className="flex items-center gap-0.5 text-xs text-muted-foreground mr-1"
                              title="Anexos"
                            >
                              <Paperclip className="h-3.5 w-3.5" />
                              {bleed.files_count}
                            </span>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Ver detalhes"
                            onClick={() => openDetail(bleed)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {isPending && canApprove && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-blue-600 border-blue-300"
                              onClick={() =>
                                setReview({ open: true, action: "approve", bleed, observation: "", saving: false })
                              }
                            >
                              Aprovar
                            </Button>
                          )}
                          {isPending && canReject && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-300"
                              onClick={() =>
                                setReview({ open: true, action: "reject", bleed, observation: "", saving: false })
                              }
                            >
                              Rejeitar
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {listData && (
        <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-sm">
          <span>
            Exibindo {listData.current_page} de {listData.last_page} páginas (Total: {listData.total})
          </span>
          {listData.last_page > 1 && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={listData.current_page <= 1}
                onClick={() => goToPage(listData.current_page - 1)}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={listData.current_page >= listData.last_page}
                onClick={() => goToPage(listData.current_page + 1)}
              >
                Próxima
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Detalhes */}
      <Dialog open={detail.open} onOpenChange={(open) => setDetail((d) => ({ ...d, open }))}>
        <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Sangria {detail.bleed?.code}</DialogTitle>
            <DialogDescription>
              {detail.bleed?.bleed_status?.name} · registrada em {formatDateTime(detail.bleed?.created_at)}
            </DialogDescription>
          </DialogHeader>

          {detail.loading || !detail.bleed ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4 text-sm min-w-0">
              <div className="grid grid-cols-2 gap-3">
                <Info label="Vendedor" value={detail.bleed.seller?.user?.name} />
                <Info label="Unidade" value={detail.bleed.unity?.name} />
                <Info label="Data da sangria" value={formatDateOnly(detail.bleed.bleed_date)} />
                <Info
                  label="Valor"
                  value={Mask.formatCurrency(Number(detail.bleed.value))}
                  className="text-green-600 font-bold"
                />
                <Info label="Registrada por" value={detail.bleed.created_by?.name} />
                {detail.bleed.reviewed_by && (
                  <Info
                    label={detail.bleed.bleed_status_id === BLEED_STATUS.APPROVED ? "Aprovada por" : "Rejeitada por"}
                    value={`${detail.bleed.reviewed_by.name} · ${formatDateTime(detail.bleed.reviewed_at)}`}
                  />
                )}
              </div>

              {detail.bleed.observation && <Info label="Observação" value={detail.bleed.observation} />}
              {detail.bleed.review_observation && (
                <Info label="Observação do financeiro" value={detail.bleed.review_observation} />
              )}

              <div>
                <p className="text-xs text-muted-foreground mb-2">Anexos</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {detail.bleed.files?.map((file) =>
                    file.mime_type?.startsWith("image/") ? (
                      <a key={file.id} href={file.url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={file.url}
                          alt={file.file_name}
                          className="h-24 w-full object-cover rounded border hover:opacity-80"
                        />
                      </a>
                    ) : (
                      <a
                        key={file.id}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-24 flex-col items-center justify-center gap-1 rounded border p-2 hover:bg-muted"
                      >
                        <FileText className="h-6 w-6 text-gray-500" />
                        <span className="text-xs truncate w-full text-center">{file.file_name}</span>
                      </a>
                    )
                  )}
                </div>
              </div>
            </div>
          )}

          {detail.bleed?.bleed_status_id === BLEED_STATUS.PENDING && (canApprove || canReject) && !detail.loading && (
            <DialogFooter className="gap-2 sm:gap-0">
              {canReject && (
                <Button
                  variant="outline"
                  className="text-red-600 border-red-300"
                  onClick={() =>
                    setReview({ open: true, action: "reject", bleed: detail.bleed, observation: "", saving: false })
                  }
                >
                  <X className="mr-1 h-4 w-4" /> Rejeitar
                </Button>
              )}
              {canApprove && (
                <Button
                  onClick={() =>
                    setReview({ open: true, action: "approve", bleed: detail.bleed, observation: "", saving: false })
                  }
                >
                  <Check className="mr-1 h-4 w-4" /> Aprovar
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Aprovar / Rejeitar */}
      <Dialog
        open={review.open}
        onOpenChange={(open) => !review.saving && setReview((r) => ({ ...r, open }))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {review.action === "approve" ? "Aprovar sangria" : "Rejeitar sangria"} {review.bleed?.code}
            </DialogTitle>
            <DialogDescription>
              {review.bleed?.seller?.user?.name} · {Mask.formatCurrency(Number(review.bleed?.value || 0))}
              <br />
              {review.action === "approve"
                ? "O valor será adicionado ao saldo de sangria do vendedor."
                : "A sangria não gera saldo e o código fica livre para uso."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Observação (opcional)</Label>
            <Textarea
              rows={3}
              maxLength={1000}
              value={review.observation}
              onChange={(e) => setReview((r) => ({ ...r, observation: e.target.value }))}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              disabled={review.saving}
              onClick={() => setReview((r) => ({ ...r, open: false }))}
            >
              Cancelar
            </Button>
            <Button
              variant={review.action === "approve" ? "default" : "destructive"}
              disabled={review.saving}
              onClick={confirmReview}
            >
              {review.saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {review.action === "approve" ? "Aprovar" : "Rejeitar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Main>
  );
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  className,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className="rounded-lg border bg-white dark:bg-[#2c2c2c] p-4 flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase text-muted-foreground">{label}</p>
        <p className={cn("text-lg sm:text-2xl font-bold truncate", className)}>{value}</p>
      </div>
      <Icon className="h-5 w-5 shrink-0 text-muted-foreground" />
    </div>
  );
}

function Info({ label, value, className }: { label: string; value?: string | null; className?: string }) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={cn("break-words", className)}>{value || "-"}</p>
    </div>
  );
}
